import {
  CFormCheck,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import TranslateIcon from "../../components/icons/TranslateIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { iconBtnStyle, tdStyle, thStyle } from "../../helpers/constants";

/* ─────────────── Row ─────────────── */

function TableBodyRowContent({
  pack,
  deletedList,
}: {
  pack: Pack;
  deletedList: React.Dispatch<BulkDeleteActions>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const deletePack = useMutation((id: string) => {
    const endpoint = isAdmin
      ? `packs/${id}`
      : `packs/${id}?placeId=${selectedPlaceId}`;

    return apiService.MakeDeleteRequest(endpoint, token);
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: pack.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: pack.id });
    }
  }, [deletedList, isChecked, pack.id]);

  const removePack = () => {
    deletePack.mutate(pack.id, {
      onError(error) {
        console.log(error);
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.packDeleted);
        queryClient.invalidateQueries("getAllPacks");
      },
    });
  };

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Image + title */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 220 }}
        >
          <img
            src={
              pack?.image ??
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                pack?.title ?? "",
              )}`
            }
            alt=""
            width={44}
            height={44}
            className="flex-shrink-0"
            style={{ borderRadius: 10, objectFit: "cover" }}
          />
          <Link
            to={`/packs/update/${pack.id}`}
            className="fw-semibold text-truncate"
            style={{ maxWidth: 280 }}
            title={pack?.title}
          >
            {pack?.title}
          </Link>
        </div>
      </CTableDataCell>

      {/* Place */}
      <CTableDataCell style={tdStyle}>
        {pack?.place?.name ? (
          <span className="text-capitalize">{pack.place.name}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Price */}
      <CTableDataCell style={tdStyle}>
        {pack?.price != null ? (
          <span className="fw-bold">{pack.price}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <Link
            to={`/packs/update/${pack.id}`}
            title="Modifier"
            aria-label="Modifier"
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={iconBtnStyle}
          >
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </Link>

          {isAdmin && (
            <Link
              to="/pack-translations/new"
              state={{ pack }}
              title="Traduire"
              aria-label="Traduire"
              className="d-inline-flex align-items-center justify-content-center rounded-circle"
              style={iconBtnStyle}
            >
              <TranslateIcon width="18" height="18" fill="#000" />
            </Link>
          )}

          <button
            type="button"
            title="Supprimer"
            aria-label="Supprimer"
            onClick={() => setShowModal(true)}
            disabled={deletePack.isLoading}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{ ...iconBtnStyle, backgroundColor: "rgba(220,53,69,0.10)" }}
          >
            {deletePack.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={() => removePack()}
        onClose={() => setShowModal(false)}
      />
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  packs: Pack[];
  packsToBeDeleted: React.Dispatch<BulkDeleteActions>;
}

export default function PacksTable({ packs, packsToBeDeleted }: TableProps) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <CTableHeaderCell scope="col" style={thStyle}>
              Titre
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Établissement
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Prix
            </CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              style={{ ...thStyle, textAlign: "right" }}
            >
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {packs?.map((pack) => (
            <TableBodyRowContent
              pack={pack}
              key={pack?.id}
              deletedList={packsToBeDeleted}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
